import BinaryOperatorRadioGroup from "components/financials/modern/BinaryOperatorRadioGroup";
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import { FilterableIndicatorsList } from "components/screener/v2/filters/FilterableIndicatorsList";
import { NumericFilterRuleDefinition } from "components/screener/v2/filters/NumericFilterRuleDefinition";
import { RichUIFilterRuleDefinition } from "components/screener/v2/filters/RichUIFilterRuleDefinition";
import { genFilterButtonId } from "components/screener/v2/filters/focus";
import type { FilterCondition } from "components/screener/v2/filters/types";
import classNames from "components/ui/classNames";
import stringHashCode from "lib/utils/stringHashCode";
import { ListFilterIcon } from "lucide-react";
import { useIntl } from "react-intl";
import type { IScreenerContextInput } from "types/graphqlTypes";
import { IIndicatorKind } from "types/graphqlTypes";

interface FiltersBarProps {
  disabled?: boolean;
  screenerContext: IScreenerContextInput;
  activeFilters: FilterableIndicator[];
  selectedIndicators: FilterableIndicator[];
  conditions: FilterCondition[];
  pipelineBinaryOperator: "AND" | "OR";
  onSelect: (indicator: FilterableIndicator, isSelected: boolean) => void;
  onSelectMany: (indicators: FilterableIndicator[]) => void;
  onConditionsChange: (conditions: FilterCondition[]) => void;
  onChangePipelineBinaryOperator: (operator: "AND" | "OR") => void;
}
export function FiltersBar({
  disabled,
  screenerContext,
  activeFilters,
  selectedIndicators,
  conditions,
  pipelineBinaryOperator,
  onSelect,
  onSelectMany,
  onConditionsChange,
  onChangePipelineBinaryOperator,
}: FiltersBarProps) {
  const intl = useIntl();
  const conditionsHashCode = stringHashCode(JSON.stringify(conditions));
  return (
    <div className="flex items-center landscape:max-lg:hidden">
      <div
        className={classNames(
          "flex w-full items-center gap-x-2 rounded-2xl bg-slate-700/5 p-2",
          "dark:border-slate-700 dark:bg-slate-900",
          "z-30",
        )}
      >
        <ol className="flex flex-wrap items-center gap-2">
          <li className="flex">
            <FilterableIndicatorsList
              disabled={disabled}
              activeFilters={activeFilters}
              filterableIndicators={selectedIndicators}
              onSelectMany={onSelectMany}
            />
          </li>

          {activeFilters.map((indicator) => {
            if (indicator.__typename === "ScreenerServerSideIndicator") {
              return null;
            }
            const currentCondition = conditions.find(({ identifier }) => identifier === indicator.objectId);
            const isRichUIFilterIndicator =
              indicator.__typename === "ScreenerStaticIndicator" &&
              (indicator.kind === IIndicatorKind.Text ||
                indicator.kind === IIndicatorKind.Boolean ||
                indicator.kind === IIndicatorKind.Date);

            const triggerButton = (
              <button
                type="button"
                id={genFilterButtonId(indicator)}
                className={classNames(
                  "inline-flex h-8 items-center gap-x-2 rounded-full px-3 text-sm font-bold",
                  "border border-navy-600/10 bg-navy-400/10 text-navy-600 hover:bg-navy-600/10",
                  "dark:border dark:border-navy-400/40 dark:bg-navy-400/20 dark:text-navy-300 dark:hover:bg-navy-400/40",
                  {
                    "border border-chestnut-600/20 bg-chestnut-100 text-chestnut-600 hover:bg-chestnut-200/40 hover:text-chestnut-600":
                      currentCondition,

                    "dark:border dark:border-chestnut-400/40 dark:bg-chestnut-400/20 dark:text-chestnut-300 dark:hover:bg-chestnut-400/40 dark:hover:text-chestnut-200":
                      currentCondition,
                  },
                )}
              >
                <span>{indicator.abbreviation}</span>
                {currentCondition && (
                  <span
                    title={intl.formatMessage({ defaultMessage: "Filtro Ativo", id: "AhRoRG" })}
                    aria-label={intl.formatMessage(
                      { defaultMessage: "Filtro Ativo: {name}", id: "IDzB91" },
                      { name: indicator.name },
                    )}
                  >
                    <ListFilterIcon absoluteStrokeWidth={true} className="size-4" strokeWidth={3} />
                  </span>
                )}
              </button>
            );

            if (isRichUIFilterIndicator) {
              return (
                <li key={indicator.objectId} className="flex">
                  <RichUIFilterRuleDefinition
                    key={conditionsHashCode}
                    hideCounts={conditions.length > 0 && activeFilters.length > 1}
                    screenerContext={screenerContext}
                    condition={currentCondition?.type === "COMPARISON" ? currentCondition : null}
                    indicator={indicator}
                    onRemoveFilter={() => onSelect(indicator, true)}
                    onChange={(input) => {
                      if (input === null) {
                        onConditionsChange(conditions.filter(({ identifier }) => identifier !== indicator.objectId));
                        return;
                      }
                      onConditionsChange(
                        conditions
                          .filter(({ identifier }) => identifier !== indicator.objectId)
                          .concat([
                            {
                              identifier: indicator.objectId,
                              type: "COMPARISON",
                              operator: input.condition.operator,
                              value: input.condition.value,
                            },
                          ]),
                      );
                    }}
                  >
                    {triggerButton}
                  </RichUIFilterRuleDefinition>
                </li>
              );
            }
            return (
              <li key={indicator.objectId} className="flex">
                <NumericFilterRuleDefinition
                  key={conditionsHashCode}
                  indicator={indicator}
                  condition={currentCondition}
                  onRemoveFilter={() => onSelect(indicator, true)}
                  onChange={(input) => {
                    if (!input) {
                      onConditionsChange(conditions.filter(({ identifier }) => identifier !== indicator.objectId));
                      return;
                    }
                    if (input.condition.type === "COMPARISON") {
                      onConditionsChange(
                        conditions
                          .filter(({ identifier }) => identifier !== indicator.objectId)
                          .concat({
                            identifier: indicator.objectId,
                            type: "COMPARISON",
                            operator: input.condition.operator,
                            value: input.condition.value,
                            expression: input.expression,
                          }),
                      );
                    }
                    if (input.condition.type === "RANGE") {
                      onConditionsChange(
                        conditions
                          .filter(({ identifier }) => identifier !== indicator.objectId)
                          .concat({
                            identifier: indicator.objectId,
                            type: "RANGE",
                            minValue: input.condition.minValue,
                            maxValue: input.condition.maxValue,
                            expression: input.expression,
                          }),
                      );
                    }
                  }}
                >
                  {triggerButton}
                </NumericFilterRuleDefinition>
              </li>
            );
          })}

          {activeFilters.length > 1 && conditions.length > 1 && (
            <li className="flex">
              <BinaryOperatorRadioGroup
                disabled={false}
                value={pipelineBinaryOperator}
                onChange={onChangePipelineBinaryOperator}
              />
            </li>
          )}
        </ol>
      </div>
    </div>
  );
}
